import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  CheckCircle2,
  CalendarIcon,
  Phone,
  Video,
  Mail,
} from "lucide-react";
import { toast } from "sonner";
import { createLead } from "@/lib/api";

const TIMES = [
  "09:00 AM",
  "10:00 AM",
  "11:00 AM",
  "12:00 PM",
  "01:00 PM",
  "02:00 PM",
  "03:00 PM",
  "04:00 PM",
  "05:00 PM",
];

const CONTACT_METHODS = [
  { id: "call", label: "Phone Call", icon: Phone },
  { id: "video", label: "Video Call", icon: Video },
  { id: "email", label: "Email", icon: Mail },
];

function formatDate(d) {
  if (!d) return "";
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function BookCallDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    business_name: "",
    current_website: "",
    sample_website: "",
    project_details: "",
    preferred_contact: "call",
  });
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const setField = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const reset = () => {
    setForm({
      name: "",
      email: "",
      phone: "",
      business_name: "",
      current_website: "",
      sample_website: "",
      project_details: "",
      preferred_contact: "call",
    });
    setDate(null);
    setTime("");
    setDone(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    if (!date) {
      toast.error("Pick a preferred date.");
      return;
    }
    if (!time) {
      toast.error("Pick a preferred time.");
      return;
    }
    try {
      setLoading(true);
      const preferred_date = date.toISOString().slice(0, 10);
      await createLead({
        ...form,
        preferred_date,
        preferred_time: time,
        source: "consultation",
        project_details:
          (form.project_details || "") +
          `\n\n[Requested consultation on ${formatDate(date)} at ${time} via ${form.preferred_contact}]`,
      });
      setDone(true);
      toast.success("Booked! We'll confirm shortly.");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not book. Try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) setTimeout(reset, 250);
      }}
    >
      <DialogContent
        data-testid="book-call-dialog"
        className="bg-[#0a0a0a] border-[#27272A] text-white sm:max-w-[600px] max-h-[92vh] overflow-y-auto"
      >
        {!done ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-3xl font-black tracking-tight">
                Book a <span className="text-gradient-neon">free strategy call.</span>
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                Tell us about your project — we’ll send a custom quote and confirm a time.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="space-y-4 mt-2">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest font-mono-jb">
                    Name *
                  </Label>
                  <Input
                    data-testid="book-name"
                    value={form.name}
                    onChange={setField("name")}
                    className="mt-1 bg-[#111] border-[#27272A] focus-visible:ring-[#CCFF00]"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest font-mono-jb">
                    Email *
                  </Label>
                  <Input
                    data-testid="book-email"
                    type="email"
                    value={form.email}
                    onChange={setField("email")}
                    className="mt-1 bg-[#111] border-[#27272A] focus-visible:ring-[#CCFF00]"
                    required
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest font-mono-jb">
                    Phone
                  </Label>
                  <Input
                    data-testid="book-phone"
                    value={form.phone}
                    onChange={setField("phone")}
                    className="mt-1 bg-[#111] border-[#27272A] focus-visible:ring-[#CCFF00]"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest font-mono-jb">
                    Business
                  </Label>
                  <Input
                    data-testid="book-business"
                    value={form.business_name}
                    onChange={setField("business_name")}
                    className="mt-1 bg-[#111] border-[#27272A] focus-visible:ring-[#CCFF00]"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest font-mono-jb">
                    Current Website
                  </Label>
                  <Input
                    data-testid="book-current-website"
                    type="url"
                    value={form.current_website}
                    onChange={setField("current_website")}
                    placeholder="https://your-site.com"
                    className="mt-1 bg-[#111] border-[#27272A] focus-visible:ring-[#CCFF00]"
                  />
                  <p className="mt-1 text-[11px] text-zinc-500">
                    Optional — if you already have one.
                  </p>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest font-mono-jb">
                    Sample Website
                  </Label>
                  <Input
                    data-testid="book-sample-website"
                    type="url"
                    value={form.sample_website}
                    onChange={setField("sample_website")}
                    placeholder="https://a-site-you-love.com"
                    className="mt-1 bg-[#111] border-[#27272A] focus-visible:ring-[#CCFF00]"
                  />
                  <p className="mt-1 text-[11px] text-zinc-500">
                    Optional — a site you&apos;d like yours to look like.
                  </p>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest font-mono-jb">
                    Preferred Date *
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        data-testid="book-date-trigger"
                        type="button"
                        variant="outline"
                        className="mt-1 w-full justify-start text-left font-normal bg-[#111] border-[#27272A] hover:bg-[#111] hover:text-white"
                      >
                        <CalendarIcon className="w-4 h-4 mr-2 text-[#CCFF00]" />
                        {date ? formatDate(date) : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="bg-[#0a0a0a] border-[#27272A] p-0 w-auto">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(d) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          return d < today;
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest font-mono-jb">
                    Preferred Time *
                  </Label>
                  <Select value={time} onValueChange={setTime}>
                    <SelectTrigger
                      data-testid="book-time"
                      className="mt-1 bg-[#111] border-[#27272A]"
                    >
                      <SelectValue placeholder="Select a time" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-[#27272A] text-white">
                      {TIMES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest font-mono-jb">
                  How should we contact you?
                </Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {CONTACT_METHODS.map((m) => {
                    const Icon = m.icon;
                    const sel = form.preferred_contact === m.id;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        data-testid={`book-contact-${m.id}`}
                        onClick={() => setField("preferred_contact")(m.id)}
                        className={`rounded-xl border-2 p-3 flex flex-col items-center gap-1 text-xs transition-all ${
                          sel
                            ? "border-[#CCFF00] bg-[#CCFF00]/5 text-[#CCFF00]"
                            : "border-[#27272A] bg-[#111] text-zinc-400 hover:border-white/30"
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {m.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest font-mono-jb">
                  Project Details
                </Label>
                <Textarea
                  data-testid="book-details"
                  value={form.project_details}
                  onChange={setField("project_details")}
                  placeholder="Tell us about your business and goals…"
                  className="mt-1 bg-[#111] border-[#27272A] focus-visible:ring-[#CCFF00] min-h-[80px]"
                />
              </div>

              <Button
                type="submit"
                data-testid="book-submit"
                disabled={loading}
                className="btn-neon rounded-none w-full h-12"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Book Strategy Call"
                )}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#CCFF00]/10 border border-[#CCFF00]/30 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-[#CCFF00]" />
            </div>
            <h3 className="font-display text-2xl font-bold mt-6">
              Call requested!
            </h3>
            <p className="text-zinc-400 mt-2">
              We’ll confirm your time via {form.preferred_contact} within 24 hours.
            </p>
            <Button
              onClick={() => onOpenChange(false)}
              className="btn-neon rounded-none mt-8 px-8"
              data-testid="book-close"
            >
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
